import { Box, CircularProgress } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import { useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useCalendarSync } from '../../../../customHook/useCalendarSync';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import {
  clearPlatformEvents,
  fetchGoogleEvents,
  fetchHuddleEvents,
  fetchMsTeamsEvents,
  fetchZoomEvents,
  toggleFilter,
  updateConnectionStatus,
} from '../../../../store/slices/calendarSlice';
import zeplinColor from '../../../../theme/Color';
import { calculateWeekRange, getTimezoneDisplayText } from '../../../../utilities/CalendarUtils';
import CustomAlert from '../../../shared/CustomAlert/CustomAlert';
import CustomAlertContain from '../../../shared/CustomAlert/CustomAlertContain';
import { CalendarGrid } from '../CalendarGrid/CalendarGrid';

dayjs.extend(weekOfYear);
dayjs.extend(isoWeek);

interface CalendarMainProps {
  selectedDateRange?: {
    startDate: Dayjs;
    endDate: Dayjs;
  };
  selectedDate: dayjs.Dayjs | null;
}

const CalendarMain = ({ selectedDate }: CalendarMainProps) => {
  const client = useLocation();
  const clientId = client.state?.client.AccountId;
  const { accounts } = useAppSelector((state) => state.calendar);
  const dateRange = calculateWeekRange(selectedDate || dayjs());
  const dispatch = useAppDispatch();
  const { filteredEvents, loadingStates, connections } = useAppSelector((state) => state.calendar);
  const { isGoogleConnected, isZoomConnected, isMsConnected } = connections;
  const isAnythingLoading = Object.values(loadingStates).some((status) => status === true);

  useCalendarSync({
    onConnectionChange: useCallback(
      (e: CustomEvent) => {
        const { key, value, data } = e.detail;
        const isConnected = value === 'true';
        dispatch(updateConnectionStatus({ [key]: isConnected }));

        if (key === 'MSToken' && data) {
          dispatch(fetchMsTeamsEvents(data));
        }
      },
      [dispatch],
    ),
    onToolSelect: (e: CustomEvent) => {
      const { key, value } = e.detail;
      const isVisible = value === 'true';
      dispatch(toggleFilter({ tool: key, isVisible }));
    },
  });

  useEffect(() => {
    const month = (selectedDate?.month() || dayjs().month()) + 1;
    const year = selectedDate?.year();

    if (isGoogleConnected) {
      dispatch(fetchGoogleEvents({ month, year }));
    }
    if (isZoomConnected) {
      dispatch(fetchZoomEvents({ month, year }));
    }
    if (isMsConnected) {
      dispatch(fetchMsTeamsEvents());
    }
    dispatch(fetchHuddleEvents(clientId));
  }, [
    dispatch,
    accounts['googleAccounts' as keyof typeof accounts],
    accounts['zoomAccounts' as keyof typeof accounts],
    accounts['msTeamsAccounts' as keyof typeof accounts],
    selectedDate,
    clientId,
  ]);

  return (
    <Box
      sx={{
        border: `1px solid ${zeplinColor.Neutral5}`,
        borderTopLeftRadius: '14px',
        borderTopRightRadius: '14px',
        backgroundColor: zeplinColor.Neutral1,
        height: { xs: 'calc(100vh - 300px)', xl: 'calc(100vh - 360px)' },
        overflowX: 'auto',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ flex: 1, overflowX: 'auto' }}>
        <CalendarGrid
          startDate={dateRange.startDate}
          endDate={dateRange.endDate}
          selectedDate={selectedDate}
          timeSlotHeight={60}
          startHour={0}
          endHour={23}
          getTimezoneDisplayText={() => getTimezoneDisplayText(filteredEvents)}
        />
      </Box>
      <CustomAlert
        open={isAnythingLoading}
        handleClose={() => {}}
        message={
          <CustomAlertContain
            alertIcon={<CircularProgress sx={{ color: zeplinColor.Neutral1 }} size={'18px'} />}
            alert={isAnythingLoading ? 'Fetching Events...' : 'Events Fetched!'}
          />
        }
        error={false}
      />
    </Box>
  );
};

export default CalendarMain;
